import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Save, Loader2 } from "lucide-react";
import workvivoNotificationsService from "../../services/workvivoNotificationsService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import { useTranslation } from "../../i18n";

const TYPE_LABEL_KEYS = {
  recognition_created: "notification_matrix.types.recognition_created",
  recognition_approved: "notification_matrix.types.recognition_approved",
  recognition_rejected: "notification_matrix.types.recognition_rejected",
  nomination_submitted: "notification_matrix.types.nomination_submitted",
  nomination_approved: "notification_matrix.types.nomination_approved",
  campaign_started: "notification_matrix.types.campaign_started",
  campaign_ended: "notification_matrix.types.campaign_ended",
  budget_exhausted: "notification_matrix.types.budget_exhausted",
  wallet_credited: "notification_matrix.types.wallet_credited",
  reward_redeemed: "notification_matrix.types.reward_redeemed",
  approval_pending: "notification_matrix.types.approval_pending",
  system_alert: "notification_matrix.types.system_alert",
};

const WorkvivoNotificationMatrix = ({ orgId }) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation("admin_integrations");
  const [rows, setRows] = useState([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["workvivo-notifications", "config", orgId || "self"],
    queryFn: () => workvivoNotificationsService.getConfig(orgId),
  });

  // Seed local edits from the server once per org; later background
  // refetches must not wipe unsaved changes.
  const seededOrgRef = useRef(null);
  useEffect(() => {
    const key = orgId || "self";
    if (data && seededOrgRef.current !== key) {
      setRows(data);
      seededOrgRef.current = key;
    }
  }, [data, orgId]);

  const saveMutation = useMutation({
    mutationFn: (next) => workvivoNotificationsService.updateConfig(next, orgId),
    onSuccess: (updated) => {
      setRows(updated);
      queryClient.invalidateQueries({ queryKey: ["workvivo-notifications", "config"] });
      toast.success(t("notification_matrix.saved"));
    },
    onError: (err) => toast.error(formatError(err)),
  });

  const dirty = useMemo(() => {
    if (!data) return false;
    return JSON.stringify(data) !== JSON.stringify(rows);
  }, [data, rows]);

  const toggle = (type, field) => {
    setRows((prev) =>
      prev.map((r) => (r.type === type ? { ...r, [field]: !r[field] } : r)),
    );
  };

  const typeLabel = (type) =>
    t(TYPE_LABEL_KEYS[type] || `notification_matrix.types.${type}`, { defaultValue: type });

  // The scope names are code, not prose: split the sentence around them so
  // they keep their <code> styling in every language.
  const scopeParts = t("notification_matrix.scopes_warning").split(/(\{\{scope1\}\}|\{\{scope2\}\})/);
  const scopeSlots = {
    "{{scope1}}": "notifications.write",
    "{{scope2}}": "notifications.users.write",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t("notification_matrix.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
        {formatError(error)}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t("notification_matrix.title")}
          </h3>
        </div>
        <button
          type="button"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate(rows)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {t("common:actions.save")}
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-2">
        {t("notification_matrix.description")}
      </p>
      <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
        <strong>{t("notification_matrix.heads_up")}</strong>{" "}
        {scopeParts.map((part, i) =>
          scopeSlots[part] ? (
            <code key={i} className="px-1 py-0.5 bg-amber-100 rounded text-xs">
              {scopeSlots[part]}
            </code>
          ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
          )
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">{t("notification_matrix.table.type")}</th>
              <th className="py-2 px-2 font-medium text-center">{t("notification_matrix.table.enabled")}</th>
              <th className="py-2 px-2 font-medium text-center">{t("notification_matrix.table.push")}</th>
              <th className="py-2 px-2 font-medium text-center">{t("notification_matrix.table.email")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.type} className="border-b last:border-b-0">
                <td className="py-2 pr-4 text-gray-800">
                  {typeLabel(row.type)}
                </td>
                {["enabled", "push", "email"].map((field) => (
                  <td key={field} className="py-2 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!row[field]}
                      onChange={() => toggle(row.type, field)}
                      disabled={field !== "enabled" && !row.enabled}
                      className="w-4 h-4 accent-indigo-600 disabled:opacity-40"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkvivoNotificationMatrix;
